import api from './api';

/**
 * سرویس «مرکز اسنپ‌پی» — آینهٔ `figano-backend/src/routes/admin/snapppay.routes.ts`
 * (apiService پاسخ `{status, data}` را باز می‌کند و مستقیماً `data` را برمی‌گرداند)
 */

export type SnappPayStatus = 'PENDING' | 'VERIFY' | 'SETTLE' | 'CANCEL' | 'REVERT';

export const SNAPP_STATUS_LABELS: Record<string, string> = {
  PENDING: 'در انتظار تأیید (PENDING)',
  VERIFY: 'تأیید شده — منتظر نهایی‌سازی (VERIFY)',
  SETTLE: 'نهایی شده (SETTLE)',
  CANCEL: 'لغو / مسترد شده (CANCEL)',
  REVERT: 'برگشت‌خورده قبل از نهایی‌سازی (REVERT)',
  UNKNOWN: 'نامشخص',
};

export const SNAPP_STATUS_COLORS: Record<string, string> = {
  PENDING: '#D97706',
  VERIFY: '#2563EB',
  SETTLE: '#16A34A',
  CANCEL: '#DC2626',
  REVERT: '#9333EA',
  UNKNOWN: '#64748B',
};

export interface SnappPaySettings {
  enabled: boolean;
  eligibilityMode: 'off' | 'soft' | 'strict';
  minAmount: number;
  maxAmount: number;
  commissionType: number;
  forcedMethodTypes: string[];
  itemCategory: string;
}

export interface SnappPayOverview {
  configured: boolean;
  apiBase: string;
  returnUrl: string;
  environment: 'staging' | 'production';
  settings: SnappPaySettings;
  surcharge: { percent: number; mode: 'on_base' | 'gross_up' };
  /** تنظیم «در استرداد، کارمزد اقساطی هم به خریدار برگردد» (پیش‌فرض دیالوگ لغو/کاهش مبلغ) */
  refundIncludesSurcharge?: boolean;
  counts: Record<string, { count: number; amount: number; surcharge: number }>;
  total: number;
  unsettled: number;
  syncErrors: number;
  paidLast24h: number;
  attempts24h: Record<string, number>;
  statuses: string[];
}

export interface SnappPayOrderRow {
  _id: string;
  orderNumber: string;
  status: string;
  paymentStatus: string;
  createdAt: string;
  user: { _id: string; name?: string; phone?: string; email?: string } | null;
  totalAmount: number;
  payableAmount: number | null;
  paidAmount: number | null;
  surcharge: { amount: number; ratePercent: number; mode: string; status: string } | null;
  transactionId: string | null;
  paymentToken: string | null;
  paidAt: string | null;
  verifiedAt: string | null;
  snapppayStatus: string | null;
  snapppaySettledAt: string | null;
  snapppayCancelledAt: string | null;
  snapppaySyncError: string | null;
  refundedTotal: number;
}

export interface Paged<T> {
  docs: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface SnappPayOrderItem {
  _id: string;
  productTitle: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  discountAmount?: number;
  status: string;
  refundedAmount?: number;
  refundedQuantity?: number;
  storeName?: string;
  fulfillmentType?: string;
}

export interface SnappPayLog {
  _id: string;
  action: string;
  status: string;
  amount?: number;
  authority?: string;
  refId?: string;
  code?: number;
  message?: string;
  error?: string;
  meta?: any;
  requestBody?: any;
  responseBody?: any;
  callbackQuery?: any;
  createdAt: string;
  orderId?: { _id: string; orderNumber: string; totalAmount: number } | string | null;
  userId?: { _id: string; name?: string; phone?: string } | string | null;
}

export interface SnappPayOrderDetail {
  order: SnappPayOrderRow;
  items: SnappPayOrderItem[];
  activeItemsSum: number;
  logs: SnappPayLog[];
  /** true = فقط تلاش پرداخت اسنپ‌پی ثبت شده و پرداخت نهایی نشده (بدون عملیات مالی) */
  attemptOnly?: boolean;
  attemptNote?: string;
  capabilities: { canStatus?: boolean; canSettle: boolean; canCancel: boolean; canUpdate: boolean; canRevert: boolean };
}

export interface SnappPayConnectionTest {
  apiBase: string;
  host: string;
  configured: boolean;
  dns: { ok: boolean; addresses: string[]; error?: string };
  login: { ok: boolean; error?: string; ms: number };
  eligible: { ok: boolean; eligible?: boolean; title: string; description: string; error?: string };
  hint: string;
}

export interface SnappPayEligibleTest {
  amount: number;
  surchargeAmount: number;
  surchargePercent: number;
  payableAmount: number;
  adminAvailable: boolean;
  adminReason: string | null;
  eligible: boolean | null;
  titleMessage?: string;
  description?: string;
  error?: string;
  errorCode?: number;
  httpStatus?: number;
  ms: number;
}

export interface ActionResult {
  ok?: boolean;
  mode?: string;
  status?: string;
  message?: string;
  newAmount?: number;
  previousAmount?: number;
  refunded?: number;
  amountToman?: number;
  transactionId?: string;
}

const BASE = '/admin/snapppay';

const snapppayAdminService = {
  overview: () => api.get<SnappPayOverview>(`${BASE}/overview`),

  listOrders: (params: { page?: number; limit?: number; status?: string; search?: string; syncError?: boolean; from?: string; to?: string }) =>
    api.get<Paged<SnappPayOrderRow>>(`${BASE}/orders`, {
      params: {
        page: params.page || 1,
        limit: params.limit || 20,
        status: params.status || undefined,
        search: params.search || undefined,
        syncError: params.syncError ? 1 : undefined,
        from: params.from || undefined,
        to: params.to || undefined,
      },
    }),

  orderDetail: (orderId: string) => api.get<SnappPayOrderDetail>(`${BASE}/orders/${orderId}`),
  orderStatus: (orderId: string) => api.get<ActionResult>(`${BASE}/orders/${orderId}/status`),
  orderSettle: (orderId: string) => api.post<any>(`${BASE}/orders/${orderId}/settle`, {}),
  orderCancel: (orderId: string, reason?: string, includeSurcharge?: boolean) => api.post<any>(`${BASE}/orders/${orderId}/cancel`, { reason, includeSurcharge }),
  orderUpdate: (orderId: string, amount?: number, reason?: string, includeSurcharge?: boolean) => api.post<any>(`${BASE}/orders/${orderId}/update`, { amount, reason, includeSurcharge }),
  orderRevert: (orderId: string, reason?: string) => api.post<any>(`${BASE}/orders/${orderId}/revert`, { reason }),

  listLogs: (params: { page?: number; limit?: number; action?: string; status?: string; search?: string; orderId?: string; transactionId?: string }) =>
    api.get<Paged<SnappPayLog>>(`${BASE}/logs`, {
      params: {
        page: params.page || 1,
        limit: params.limit || 25,
        action: params.action || undefined,
        status: params.status || undefined,
        search: params.search || undefined,
        orderId: params.orderId || undefined,
        transactionId: params.transactionId || undefined,
      },
    }),

  testConnection: () => api.get<SnappPayConnectionTest>(`${BASE}/tools/test-connection`),
  testEligible: (amount: number, methodTypes?: string[]) => api.post<SnappPayEligibleTest>(`${BASE}/tools/eligible`, { amount, methodTypes }),
  sweepSettle: (limit = 50) => api.post<any>(`${BASE}/tools/sweep-settle`, { limit }),
  refreshStatuses: (limit = 50) => api.post<any>(`${BASE}/tools/refresh-statuses`, { limit }),
};

export default snapppayAdminService;

/** جدول خطاهای رسمی اسنپ‌پی (SnappPay Error Codes — full version) برای راهنمای داخل پنل */
export const SNAPP_ERROR_CODES: Array<{ code: number; http: number; system: string; message: string; action: string }> = [
  { code: 1000, http: 500, system: 'GENERAL', message: 'در دریافت اطلاعات مشکلی پیش آمده است. لطفاً دوباره تلاش کنید.', action: 'خطای عمومی/شبکه. یک بار با فاصله تکرار کنید. در verify/revert بعد از تایم‌اوت ۳۰ثانیه: «استعلام وضعیت» بزنید و طبق وضعیت تصمیم بگیرید (VERIFY⇒settle، PENDING⇒verify، SETTLE⇒نهایی، REVERT⇒ناموفق). در settle: اگر وضعیت VERIFY است نادیده بگیرید، اسنپ‌پی شبانه خودش settle می‌کند.' },
  { code: 1005, http: 400, system: 'INVALID_DATA', message: 'مقدار با الگوی "+98\\d{10}" همخوانی ندارد / مقدار نباید خالی باشد', action: 'شمارهٔ موبایل بدفرمت یا paymentToken خالی. تکرار بی‌فایده است؛ داده را اصلاح کنید.' },
  { code: 1007, http: 404, system: 'NOT_FOUND', message: 'اطلاعات مورد نظر پیدا نشد.', action: 'paymentToken اشتباه است. نیاز به بررسی دستی/پنل گزارش اسنپ‌پی.' },
  { code: 1009, http: 409, system: 'DUPLICATED_DATA', message: 'تراکنش تکراری است.', action: 'transactionId قبلاً برای این فروشگاه ثبت شده. تکرار نکنید.' },
  { code: 1011, http: 400, system: 'TRANSACTION_INVALID_STATUS', message: 'وضعیت تراکنش برای عملیات موردنظر نامناسب است.', action: 'به transaction-status پاسخ نگاه کنید: REVERT=ناموفق نهایی · VERIFY=settle بزنید · SETTLE=نهایی (برای cancel/update اگر VERIFY بود اول settle) · registered=۳۰ دقیقه صبر و revert. اگر وضعیت نیامد ⇒ تراکنش به درگاه نرفته، ناموفق.' },
  { code: 1036, http: 404, system: 'ORDER_NOT_FOUND', message: 'برای تراکنش درخواست شده سفارشی یافت نشد.', action: '(update) این توکن به درگاه بانکی نرسیده. به‌روزرسانی ناموفق.' },
  { code: 1042, http: 409, system: 'UPDATING_ORDER_AMOUNT_IS_NOT_VALID', message: 'مبلغ سفارش جهت به روز رسانی می‌بایست کمتر یا مساوی مبلغ سفارش قبلی باشد.', action: 'مبلغ update باید ≤ مبلغ فعلی باشد.' },
  { code: 1048, http: 400, system: 'PAYMENT_VENTURE_MISMATCHED', message: 'امکان استفاده از سرویس اعتباری را ندارید…', action: 'توکن مال این فروشگاه نیست. نباید رخ دهد؛ با پشتیبانی اسنپ‌پی تماس بگیرید.' },
  { code: 1051, http: 403, system: 'PAYMENT_INVALID_RETURN_URL', message: 'آدرس بازگشت به سایت معتبر نمی‌باشد.', action: 'returnURL با دامنهٔ ثبت‌شده نزد اسنپ‌پی نمی‌خواند (فعلاً فقط gamizo.ir). به پشتیبانی فنی اسنپ‌پی اطلاع دهید.' },
  { code: 1053, http: 429, system: 'ONGOING_PAYMENT_TRANSITION_PROCESS', message: 'ثبت درخواست جدید ممکن نیست. لطفاً چند لحظهٔ دیگر دوباره تلاش کنید.', action: 'درخواست قبلی در حال پردازش است. چند ثانیه بعد دوباره؛ اگر 1011 گرفتید طبق وضعیت عمل کنید.' },
  { code: 1078, http: 400, system: '—', message: 'تغییر در سفارش‌هایی که از کد تخفیف اسنپ‌پی استفاده کرده‌اند ممکن نیست.', action: '(update) فقط لغو کامل (cancel) ممکن است؛ قبل از لغو از عدم ارسال کالا مطمئن شوید.' },
  { code: 100, http: 404, system: 'NOT_FOUND', message: 'اطلاعات مورد نظر پیدا نشد.', action: '(verify) paymentToken اشتباه؛ بررسی دستی.' },
];
